import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { merge, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  formatDateAsAWSDateString,
  PatientFormGroup
} from '../../../shared/patient-form/patient-form.model';
import {
  CSVPatientsFormGroup,
  FlaggedPatientRow,
  getBlockingInvalidControlKeys
} from '../import-wizard-file-received/csv-patients-form-group.model';

// Precomputed view of one flagged row, so the template binds to plain properties instead of
// re-deriving names/reasons on every change detection cycle.
interface FlaggedPatientRowView {
  rowNumber: number;
  firstName: string;
  lastName: string;
  rawDateOfBirthText: string;
  invalidFieldsText: string;
  hasInvalidDateOfBirth: boolean;
  hasSkippableInvalidField: boolean;
  hasUnskippableInvalidField: boolean;
  firstNameControl: FormControl;
  lastNameControl: FormControl;
  dateOfBirthControl: FormControl;
  patientFormGroup: PatientFormGroup;
}

@Component({
  selector: 'csi-import-wizard-review-flagged-rows',
  templateUrl: './import-wizard-review-flagged-rows.component.html',
  styleUrls: ['./import-wizard-review-flagged-rows.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportWizardReviewFlaggedRowsComponent implements OnInit, OnDestroy {
  @Input() csvPatientsFormGroup: CSVPatientsFormGroup;
  @Output() goBack: EventEmitter<null> = new EventEmitter();
  @Output() completed: EventEmitter<null> = new EventEmitter();
  @Output() cancel: EventEmitter<null> = new EventEmitter();

  public flaggedRowViews: FlaggedPatientRowView[] = [];
  // Rows missing required information (first/last name): importing them is impossible, so they
  // must be fixed inline or removed from the import.
  public mustFixRowViews: FlaggedPatientRowView[] = [];
  public displayedMustFixRowViews: FlaggedPatientRowView[] = [];
  // Rows whose only problems are invalid OPTIONAL fields: the user can correct them, or skip
  // them and the patient is still created without those fields.
  public skippableRowViews: FlaggedPatientRowView[] = [];
  public displayedSkippableRowViews: FlaggedPatientRowView[] = [];
  // Only this many rows render editors per section — a systematically bad column can flag every
  // row of a 10k-row file, and one birthday picker per row at that scale freezes the tab.
  public readonly flaggedRowDisplayLimit = 100;

  public readonly mustFixColumnsToDisplay = [
    'rowNumber',
    'firstNameInput',
    'lastNameInput',
    'csvValue',
    'correctedDateOfBirth',
    'reason',
    'mustFixAction'
  ];
  public readonly skippableColumnsToDisplay = [
    'rowNumber',
    'firstName',
    'lastName',
    'csvValue',
    'correctedDateOfBirth',
    'reason',
    'skippableAction'
  ];

  private readonly friendlyControlNameMap = {
    firstName: 'First Name',
    lastName: 'Last Name',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    healthCard: 'Health Card',
    phone: 'Phone',
    email: 'Email',
    address: 'Address'
  };

  // Required identity fields cannot be skipped-to-null; rows blocked on these sit in the
  // must-fix section until edited inline or removed.
  private readonly unskippableControlKeys = ['firstName', 'lastName'];

  private flaggedFieldEditsSubscription: Subscription;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.refreshFlaggedRows();
  }

  ngOnDestroy() {}

  public trackByRowNumber(index: number, flaggedRowView: FlaggedPatientRowView): number {
    return flaggedRowView.rowNumber;
  }

  // Skip = import the patient without the invalid optional field(s): clearing them both drops
  // the bad values and clears their validation errors, so the row becomes valid and leaves this
  // list. Required fields (first/last name) are never cleared — see removeFlaggedRow.
  public skipFlaggedRow(flaggedRowView: FlaggedPatientRowView) {
    this.clearSkippableInvalidFields(flaggedRowView.patientFormGroup);
    this.refreshFlaggedRows();
  }

  // Drop the row from the import entirely — for rows whose blocking fields cannot be cleared
  // (missing required first/last name) or that the user does not want to import at all.
  public removeFlaggedRow(flaggedRowView: FlaggedPatientRowView) {
    const patientRowControls = this.csvPatientsFormGroup.controls.patients.controls;
    const rowControlIndex = patientRowControls.indexOf(flaggedRowView.patientFormGroup);
    if (rowControlIndex !== -1) {
      patientRowControls.splice(rowControlIndex, 1);
    }
    this.refreshFlaggedRows();
  }

  // Clears the skippable invalid fields on every remaining flagged row. The blocking fields are
  // recomputed LIVE per row, so rows the user already corrected inline are left untouched and
  // must-fix rows keep waiting on their required fields.
  public skipAllRemainingFlaggedRows() {
    this.flaggedRowViews.forEach(flaggedRowView =>
      this.clearSkippableInvalidFields(flaggedRowView.patientFormGroup)
    );
    this.refreshFlaggedRows();
  }

  public continueToNextStep() {
    this.completed.emit(null);
  }

  private clearSkippableInvalidFields(patientFormGroup: PatientFormGroup) {
    getBlockingInvalidControlKeys(patientFormGroup)
      .filter(controlKey => this.unskippableControlKeys.indexOf(controlKey) === -1)
      .forEach(controlKey => {
        // reset(null) clears FormControl AND FormGroup-typed controls (healthCard) alike — a
        // setValue guarded by `instanceof FormControl` silently skipped group controls, leaving
        // such rows permanently flagged with a Skip button that did nothing.
        patientFormGroup.controls[controlKey].reset(null, { emitEvent: false });
      });
  }

  private refreshFlaggedRows() {
    // The writes above are silent and the row groups have no parent link to the patients
    // FormArray (bulk-import performance), so recompute every row group + the FormArray here.
    // This keeps validity, .value and the wizard's [completed] bindings in sync with this list.
    this.csvPatientsFormGroup.updateThisAndDescendants({ emitEvent: false });
    this.flaggedRowViews = this.csvPatientsFormGroup.flaggedPatientRows.map(flaggedRow =>
      this.toFlaggedRowView(flaggedRow)
    );
    this.mustFixRowViews = this.flaggedRowViews.filter(
      flaggedRowView => flaggedRowView.hasUnskippableInvalidField
    );
    this.skippableRowViews = this.flaggedRowViews.filter(
      flaggedRowView => !flaggedRowView.hasUnskippableInvalidField
    );
    this.displayedMustFixRowViews = this.mustFixRowViews.slice(0, this.flaggedRowDisplayLimit);
    this.displayedSkippableRowViews = this.skippableRowViews.slice(0, this.flaggedRowDisplayLimit);
    this.resubscribeToFlaggedFieldEdits();
    this.changeDetectorRef.markForCheck();
  }

  // Array-level valueChanges can never fire for inline edits (the row groups are parentless), so
  // listen on the displayed rows' own editable controls instead. Re-created on every refresh so
  // it always tracks the current flagged set.
  private resubscribeToFlaggedFieldEdits() {
    if (this.flaggedFieldEditsSubscription) {
      this.flaggedFieldEditsSubscription.unsubscribe();
      this.flaggedFieldEditsSubscription = null;
    }
    const editedControlStreams = this.displayedSkippableRowViews
      .map(flaggedRowView => flaggedRowView.dateOfBirthControl.valueChanges)
      .concat(
        this.displayedMustFixRowViews.map(
          flaggedRowView => flaggedRowView.dateOfBirthControl.valueChanges
        ),
        this.displayedMustFixRowViews.map(
          flaggedRowView => flaggedRowView.firstNameControl.valueChanges
        ),
        this.displayedMustFixRowViews.map(
          flaggedRowView => flaggedRowView.lastNameControl.valueChanges
        )
      );
    if (editedControlStreams.length === 0) {
      return;
    }
    this.flaggedFieldEditsSubscription = merge(...editedControlStreams)
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.normalizeEditedFieldValues();
        this.refreshFlaggedRows();
      });
  }

  // The birthday picker writes Date objects, but the import pipeline stores canonical
  // 'YYYY-MM-DD' strings — duplicate detection compares strict-equal against the API's string
  // values, so a Date object would turn every corrected row into a missed match (duplicate).
  // Name inputs cleared back to '' are normalized to null so an emptied required field keeps
  // blocking the row instead of slipping past the shared predicate's empty-value tolerance.
  private normalizeEditedFieldValues() {
    this.displayedSkippableRowViews
      .concat(this.displayedMustFixRowViews)
      .forEach(flaggedRowView => {
        const dateOfBirthValue = flaggedRowView.dateOfBirthControl.value;
        if (dateOfBirthValue instanceof Date && !isNaN(dateOfBirthValue.getTime())) {
          flaggedRowView.dateOfBirthControl.setValue(formatDateAsAWSDateString(dateOfBirthValue), {
            emitEvent: false
          });
        }
      });
    this.displayedMustFixRowViews.forEach(flaggedRowView => {
      if (flaggedRowView.firstNameControl.value === '') {
        flaggedRowView.firstNameControl.setValue(null, { emitEvent: false });
      }
      if (flaggedRowView.lastNameControl.value === '') {
        flaggedRowView.lastNameControl.setValue(null, { emitEvent: false });
      }
    });
  }

  private toFlaggedRowView(flaggedRow: FlaggedPatientRow): FlaggedPatientRowView {
    const patientControls = flaggedRow.patientFormGroup.controls;
    const blockingControlNames = getBlockingInvalidControlKeys(flaggedRow.patientFormGroup);
    const skippableBlockingControlNames = blockingControlNames.filter(
      controlKey => this.unskippableControlKeys.indexOf(controlKey) === -1
    );
    const dateOfBirthValue = patientControls.dateOfBirth.value;
    return {
      rowNumber: flaggedRow.rowIndex + this.csvPatientsFormGroup.csvRowNumberOffset,
      firstName: patientControls.firstName.value,
      lastName: patientControls.lastName.value,
      rawDateOfBirthText: typeof dateOfBirthValue === 'string' ? dateOfBirthValue : '',
      invalidFieldsText: blockingControlNames
        .map(controlKey =>
          this.translocoService.translate(this.friendlyControlNameMap[controlKey] || controlKey)
        )
        .join(', '),
      hasInvalidDateOfBirth: blockingControlNames.indexOf('dateOfBirth') !== -1,
      hasSkippableInvalidField: skippableBlockingControlNames.length > 0,
      hasUnskippableInvalidField:
        skippableBlockingControlNames.length !== blockingControlNames.length,
      firstNameControl: patientControls.firstName,
      lastNameControl: patientControls.lastName,
      dateOfBirthControl: patientControls.dateOfBirth,
      patientFormGroup: flaggedRow.patientFormGroup
    };
  }
}
